import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, CircularProgress, Alert, Chip, Paper, Tab, Tabs,
} from "@mui/material";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import { phappsRequest } from "@/services/apiService";
import { buildDialpadPath } from "@/config/apiConstants";

const DIALPAD_PINK = "#E63888";
const DIALPAD_PINK_TINT = "#FDE8F3";

function fmtDuration(s) {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function extractSummaryText(data) {
  if (!data) return null;
  if (typeof data === "string") return data;
  return data.summary ?? data.body ?? data.content ?? data.text ?? null;
}

function extractActionItems(data) {
  if (!data) return [];
  return Array.isArray(data.action_items) ? data.action_items : [];
}

function extractTranscriptLines(data) {
  if (!data) return [];
  const lines = Array.isArray(data.lines) ? data.lines : (Array.isArray(data) ? data : []);
  return lines
    .filter(l => l.type === "transcript")
    .map(l => ({ speaker: l.name, text: l.content, isAgent: !!l.user_id }));
}

const TranscriptBubble = ({ line }) => {
  const { isAgent } = line;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: isAgent ? "flex-start" : "flex-end", mb: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.4, mx: 0.75, color: isAgent ? DIALPAD_PINK : "#2563eb" }}>
        {line.speaker}
      </Typography>
      <Box sx={{ maxWidth: "78%" }}>
        <Paper elevation={0} sx={{
          px: 2, py: 1.25,
          borderRadius: isAgent ? "18px 18px 18px 4px" : "18px 18px 4px 18px",
          backgroundColor: isAgent ? DIALPAD_PINK_TINT : "#eff6ff",
          color: "#1e293b",
          fontSize: 14, lineHeight: 1.65,
          wordBreak: "break-word", whiteSpace: "pre-wrap",
          border: isAgent ? "1.5px solid #f9a8d4" : "1.5px solid #bfdbfe",
          boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
        }}>
          {line.text}
        </Paper>
      </Box>
    </Box>
  );
};

const CallDetailModal = ({ open, onClose, call }) => {
  const [summary, setSummary] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [transcriptError, setTranscriptError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const hasAiSummary = !!call?.has_ai_summary;
  const hasTranscript = !!call?.has_transcript;
  const autoMatched = !!call?.auto_matched;

  const tabs = [];
  if (hasAiSummary) tabs.push("summary");
  if (hasTranscript) tabs.push("transcript");
  const currentTab = tabs[activeTab] ?? null;

  useEffect(() => {
    if (!open || !call?.id) return;
    setSummary(null);
    setTranscript(null);
    setSummaryError(null);
    setTranscriptError(null);
    setActiveTab(0);

    if (hasAiSummary) {
      setLoadingSummary(true);
      phappsRequest(buildDialpadPath(`calls/${call.id}/summary`), "GET")
        .then(setSummary)
        .catch(() => setSummaryError("Could not load AI summary"))
        .finally(() => setLoadingSummary(false));
    }

    if (hasTranscript) {
      setLoadingTranscript(true);
      phappsRequest(buildDialpadPath(`calls/${call.id}/transcript`), "GET")
        .then(setTranscript)
        .catch(() => setTranscriptError("Could not load transcript"))
        .finally(() => setLoadingTranscript(false));
    }
  }, [open, call?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!call) return null;

  const summaryText = extractSummaryText(summary);
  const actionItems = extractActionItems(summary);
  const lines = extractTranscriptLines(transcript);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: "85vh", display: "flex", flexDirection: "column" } }}
    >
      <DialogTitle sx={{ pb: 1.5, borderBottom: "1px solid #e5e7eb" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ color: call.direction === "inbound" ? "#059669" : "#2563eb" }}>
            {call.direction === "inbound" ? <CallReceivedIcon /> : <CallMadeIcon />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: DIALPAD_PINK, lineHeight: 1.2 }}>
              {call.external_number || "Unknown number"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {fmtDateTime(call.start_time)} · {fmtDuration(call.duration_seconds)}
              {call.manager_name ? ` · ${call.manager_name}` : ""}
              {call.chemist_name ? ` · ${call.chemist_name}` : ""}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
            {hasAiSummary && (
              <Chip label="AI" size="small" sx={{ height: 20, fontSize: "0.65rem", backgroundColor: "#ede9fe", color: DIALPAD_PINK }} />
            )}
            {hasTranscript && (
              <Chip label="Transcript" size="small" sx={{ height: 20, fontSize: "0.65rem", backgroundColor: "#e0f2fe", color: "#0369a1" }} />
            )}
            {autoMatched && (
              <Chip label="Auto" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
            )}
          </Box>
        </Box>
      </DialogTitle>

      {tabs.length > 1 && (
        <Box sx={{ borderBottom: "1px solid #e5e7eb", px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              minHeight: 40,
              "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600, fontSize: "0.85rem" },
              "& .Mui-selected": { color: DIALPAD_PINK },
              "& .MuiTabs-indicator": { backgroundColor: DIALPAD_PINK },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <SmartToyOutlinedIcon sx={{ fontSize: 15 }} />
                  Summary
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <RecordVoiceOverOutlinedIcon sx={{ fontSize: 15 }} />
                  Transcript
                </Box>
              }
            />
          </Tabs>
        </Box>
      )}

      <DialogContent
        sx={{
          pt: 2, flex: 1, overflowY: "auto",
          backgroundColor: currentTab === "transcript" ? "#f4f6fb" : "#fff",
        }}
      >
        {tabs.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            No AI summary or transcript available for this call.
          </Typography>
        )}

        {/* Summary */}
        {currentTab === "summary" && (
          <Box>
            {tabs.length === 1 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                <SmartToyOutlinedIcon sx={{ fontSize: 18, color: DIALPAD_PINK }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: DIALPAD_PINK }}>AI Summary</Typography>
              </Box>
            )}
            {summaryError && <Alert severity="warning" sx={{ mb: 1 }}>{summaryError}</Alert>}
            {loadingSummary ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} sx={{ color: DIALPAD_PINK }} />
              </Box>
            ) : summaryText ? (
              <Box sx={{ p: 2, borderRadius: 1.5, backgroundColor: DIALPAD_PINK_TINT, border: "1px solid #f9a8d4" }}>
                <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                  {summaryText}
                </Typography>
                {actionItems.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: DIALPAD_PINK, textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Action items
                    </Typography>
                    <Box component="ul" sx={{ mt: 0.5, pl: 2.5, mb: 0 }}>
                      {actionItems.map((item, i) => (
                        <Typography component="li" key={i} variant="body2" sx={{ mb: 0.25 }}>
                          {typeof item === "string" ? item : (item.text ?? JSON.stringify(item))}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            ) : !summaryError && (
              <Typography color="text.secondary" variant="body2">Summary not yet available.</Typography>
            )}
          </Box>
        )}

        {/* Transcript */}
        {currentTab === "transcript" && (
          <Box>
            {tabs.length === 1 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                <RecordVoiceOverOutlinedIcon sx={{ fontSize: 18, color: "#0369a1" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0369a1" }}>Transcript</Typography>
              </Box>
            )}
            {transcriptError && <Alert severity="warning" sx={{ mb: 1 }}>{transcriptError}</Alert>}
            {loadingTranscript ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} sx={{ color: "#0369a1" }} />
              </Box>
            ) : lines.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", px: 0.5, py: 0.5 }}>
                {lines.map((line, i) => <TranscriptBubble key={i} line={line} />)}
              </Box>
            ) : !transcriptError && (
              <Typography color="text.secondary" variant="body2">Transcript not yet available.</Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CallDetailModal;
